import { useState } from 'react';

import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { calmLogger } from '@/utils/calmLogger';

import { useFaves } from './useFaves';

interface UseProgramFavorite {
	isFaved: boolean;
	toggleFavorite: () => Promise<void>;
}

/**
 * Favoriting for a program itself, as opposed to one of its guides.
 *
 * These are two separate backend systems, not one endpoint with two shapes: a guide fave is a
 * POST to programs/guides/{id}/faves, while a program fave is a body-discriminated POST to
 * `faves` carrying program_id (the same collection that also keys on feed_id, pack_class,
 * breathe_bubble_id and content_id). Only the latter can fave a program at all, which is why
 * this hook exists rather than passing a program id to the guide-scoped useIsFaved.
 */
export const useProgramFavorite = (programId?: string): UseProgramFavorite => {
	const apiRequest = useApi();
	const { logEvent } = useAnalytics();
	const { faves, mutate } = useFaves();

	// Optimistic value, held only for the round trip so the heart responds to the tap
	// immediately. Cleared once the refetched list can answer for itself — see toggleFavorite.
	const [pendingIsFaved, setPendingIsFaved] = useState<boolean>();

	// Truthiness, not `'faved_at' in fave`: the server always emits the key and nulls it out to
	// mean unfaved (faved_at: deleted_at ? null : updated_at), so a presence check reports every
	// row a user has ever unfaved as still faved.
	const isFavedOnServer = Boolean(
		programId && faves?.some(fave => fave.program_id === programId && fave.faved_at),
	);

	const isFaved = pendingIsFaved ?? isFavedOnServer;

	const toggleFavorite = async (): Promise<void> => {
		if (!programId) return;

		const addToFaves = !isFaved;
		setPendingIsFaved(addToFaves);

		try {
			await apiRequest({
				endpoint: 'faves',
				method: addToFaves ? 'POST' : 'DELETE',
				body: {
					program_id: programId,
				},
			});

			logEvent({
				eventName: 'Fave Button : Fave : Posted',
				eventProps: {
					program_id: programId,
				},
			});

			await mutate();
			// The refetch above now reflects the write, so drop the optimistic override rather
			// than leaving it to mask any later change to the underlying list.
			setPendingIsFaved(undefined);
		} catch (err) {
			// Drop back to what the server last told us — the write didn't land, so leaving the
			// optimistic value in place would show a filled heart for a fave that doesn't exist.
			setPendingIsFaved(undefined);
			calmLogger.error('Error favoriting program', { programId }, err);
		}
	};

	return {
		isFaved,
		toggleFavorite,
	};
};
